import { ALLOWED_IMAGE_TYPE, ALLOWED_SIZES, DEFAULT_IMAGE_SIZE } from './imageSizes';

interface IGetImagePathname {
  alpha?: boolean;
  background?: string; // RGB value to use for transparent PNGs, ex: background=255,255,255
  forceCrop: boolean;
  height?: number;
  pathname: string;
  quality?: number; // JPEG and WEBP image quality between 1-100 and divisible by 5. Defaults to 85
  size?: number; // takes the longest edge of the image and uses that as the resizing value
  sizeWidth?: number;
  sizeWidthDisabled?: boolean;
  type?: string; // overrides content negotiation based on Accepts header, options: jpeg, png, webp
  width?: number;
}

export const getImageUrl = ({
  pathname,
  forceCrop,
  sizeWidth = DEFAULT_IMAGE_SIZE,
  sizeWidthDisabled = false,
  width,
  height,
  size,
  type,
  background,
  alpha,
  quality,
}: IGetImagePathname) => {
  if (/missing.png/.test(pathname)) {
    return {
      path: '/placeholders/dash.png',
      query: '',
    };
  }

  const splitPathname = pathname.split('/');
  const shouldCrop = forceCrop || splitPathname[1] === 'crop';

  // filter out crop and sizeInstruction from pathname
  const reconstructedPathname = splitPathname
    .filter((pathnameSegment, index) => {
      const isSizeInstruction =
        index <= 2 && ALLOWED_SIZES.includes(Number.parseInt(pathnameSegment, 10));
      return pathnameSegment && pathnameSegment !== 'crop' && !isSizeInstruction;
    })
    .join('/');

  const query = [
    `${shouldCrop ? `action=crop` : ''}`,
    `${
      !sizeWidthDisabled && sizeWidth && !width && ALLOWED_SIZES.includes(sizeWidth)
        ? `width=${sizeWidth}`
        : ''
    }`,
    `${width && ALLOWED_SIZES.includes(width) ? `width=${width}` : ''}`,
    `${height && ALLOWED_SIZES.includes(height) ? `height=${height}` : ''}`,
    `${size && ALLOWED_SIZES.includes(size) ? `size=${size}` : ''}`,
    `${type && ALLOWED_IMAGE_TYPE.includes(type.toLowerCase()) ? `type=${type}` : ''}`,
    `${background ? `background=${background}` : ''}`,
    `${alpha?.toString() ? `alpha=${alpha.toString()}` : ''}`,
    `${quality ? `quality=${quality}` : ''}`,
  ]
    .filter(Boolean)
    // query must be in ABC-order (for CDN caching) or else the API will reject it
    .sort()
    .join('&')
    // the API is enforcing lowercase for query params (for CDN caching)
    .toLowerCase();

  // go-image-v2 service is prefixed with `/transform/v1`
  return {
    path: `/transform/v1/${reconstructedPathname}`,
    query,
  };
};
